/**
 * THE CALCULATION ENGINE.
 *
 * `computeDocument()` is the only function in DNR Vyapar 2.0 that produces a
 * number on an invoice. The editor, the preview, the print document, the PDF
 * and every report consume its output. Nobody adds up a column.
 *
 * Pure: no DOM, no I/O, no clock, no randomness, no locale. `docDate` and
 * `financialYear` are always passed in by the caller, which is what makes the
 * same input produce the same output forever, on any machine.
 *
 * Pipeline (design §5.10) — each stage rounded once, in this order:
 *   1 supply type · 2 per line · 3 document discount pro-rata
 *   4 re-derive tax on the final taxable value · 5 charges
 *   6 aggregates (SUM OF ROUNDED) · 7 TCS · 8 round-off · 9 tax summary
 */
import type { Money } from '../money/types';
import { asMoney } from '../money/types';
import {
  roundPaise,
  percentOfMoney,
  taxOnMoney,
  clampNonNegative,
  allocate,
  sumQuantity,
} from '../money/paise';
import { numberToIndianWords } from '../money/words';
import { resolvePlaceOfSupply, splitGst } from '../gst/split';
import type { SupplyType, TaxSplit } from '../gst/split';
import { CALC_VERSION } from './version';
import type {
  CalcWarning,
  ComputedDocument,
  ComputedLine,
  DocumentInput,
  LineInput,
  TaxBucket,
} from './types';

const ZERO = asMoney(0);

/** Line discount in paise. PERCENT: milli-percent. AMOUNT: paise. */
function computeLineDiscount(line: LineInput, grossPaise: Money): Money {
  if (line.discountType === 'PERCENT') return percentOfMoney(grossPaise, line.discountValue);
  if (line.discountType === 'AMOUNT') return clampNonNegative(asMoney(Math.trunc(line.discountValue)));
  return ZERO;
}

function sumLines(lines: readonly ComputedLine[], pick: (l: ComputedLine) => Money): Money {
  let total = 0;
  for (const l of lines) total += pick(l);
  return asMoney(total);
}

/**
 * Stages 2 and 4. Called twice per line: provisionally, then finally once the
 * pro-rata document discount is known — because tax must be computed on the
 * FINAL taxable value, not the pre-discount one.
 */
function computeLine(
  line: LineInput,
  index: number,
  supplyType: SupplyType,
  allocatedDocDiscountPaise: number,
  warnings: CalcWarning[],
): ComputedLine {
  const grossPaise = roundPaise((line.quantity * line.ratePaise) / 1000);
  const discountPaise = computeLineDiscount(line, grossPaise);

  if (discountPaise > grossPaise) {
    warnings.push({
      code: 'DISCOUNT_EXCEEDS_LINE',
      lineIndex: index,
      message: `Line ${index + 1}: discount exceeds the line amount; taxable clamped to zero`,
    });
  }

  const rawTaxable = grossPaise - discountPaise - allocatedDocDiscountPaise;
  if (rawTaxable < 0) {
    warnings.push({
      code: 'NEGATIVE_TAXABLE',
      lineIndex: index,
      message: `Line ${index + 1}: discounts exceed the line amount; taxable clamped to zero`,
    });
  }
  const taxablePaise = clampNonNegative(asMoney(rawTaxable));

  const tax = splitGst(taxablePaise, line.taxRateBp, supplyType);
  const cessPaise = taxOnMoney(taxablePaise, line.cessRateBp);

  return Object.freeze({
    index,
    ...(line.id !== undefined ? { lineId: line.id } : {}),
    quantity: line.quantity,
    freeQuantity: line.freeQuantity,
    ratePaise: line.ratePaise,
    grossPaise,
    discountPaise,
    allocatedDocDiscountPaise: asMoney(allocatedDocDiscountPaise),
    taxablePaise,
    taxRateBp: line.taxRateBp,
    cgstRateBp: tax.cgstRateBp,
    sgstRateBp: tax.sgstRateBp,
    igstRateBp: tax.igstRateBp,
    cgstPaise: tax.cgstPaise,
    sgstPaise: tax.sgstPaise,
    igstPaise: tax.igstPaise,
    cessRateBp: line.cessRateBp,
    cessPaise,
    totalPaise: asMoney(
      taxablePaise + tax.cgstPaise + tax.sgstPaise + tax.igstPaise + cessPaise,
    ),
    // Display-only, computed HERE so no renderer can disagree. 1.6.x computed
    // this inside the render function, which is how "Rate incl. Tax" came to
    // differ between print and PDF.
    rateWithTaxPaise: roundPaise(
      (line.ratePaise * (10_000 + line.taxRateBp + line.cessRateBp)) / 10_000,
    ),
  });
}

/** Stage 9. Sorted by rateBp so output is byte-stable across runs. */
function buildTaxSummary(
  lines: readonly ComputedLine[],
  chargeTax?: { rateBp: number; taxablePaise: Money; split: TaxSplit },
): TaxBucket[] {
  const buckets = new Map<number, TaxBucket>();
  for (const line of lines) {
    const rate = line.taxRateBp;
    const prev = buckets.get(rate) ?? {
      rateBp: rate,
      taxablePaise: ZERO,
      cgstPaise: ZERO,
      sgstPaise: ZERO,
      igstPaise: ZERO,
      cessPaise: ZERO,
    };
    buckets.set(rate, {
      rateBp: rate,
      taxablePaise: asMoney(prev.taxablePaise + line.taxablePaise),
      cgstPaise: asMoney(prev.cgstPaise + line.cgstPaise),
      sgstPaise: asMoney(prev.sgstPaise + line.sgstPaise),
      igstPaise: asMoney(prev.igstPaise + line.igstPaise),
      cessPaise: asMoney(prev.cessPaise + line.cessPaise),
    });
  }

  // Fold charge tax into its own bucket so Σ buckets === document totals.
  // A report that sums buckets must still reconcile with the invoice.
  if (chargeTax && (chargeTax.taxablePaise > 0 || chargeTax.split.igstPaise > 0)) {
    const rate = chargeTax.rateBp;
    const prev = buckets.get(rate) ?? {
      rateBp: rate,
      taxablePaise: ZERO,
      cgstPaise: ZERO,
      sgstPaise: ZERO,
      igstPaise: ZERO,
      cessPaise: ZERO,
    };
    buckets.set(rate, {
      rateBp: rate,
      taxablePaise: asMoney(prev.taxablePaise + chargeTax.taxablePaise),
      cgstPaise: asMoney(prev.cgstPaise + chargeTax.split.cgstPaise),
      sgstPaise: asMoney(prev.sgstPaise + chargeTax.split.sgstPaise),
      igstPaise: asMoney(prev.igstPaise + chargeTax.split.igstPaise),
      cessPaise: prev.cessPaise,
    });
  }

  return [...buckets.values()].sort((a, b) => b.rateBp - a.rateBp).map((b) => Object.freeze(b));
}

export function computeDocument(input: DocumentInput): ComputedDocument {
  const warnings: CalcWarning[] = [];
  if (input.lines.length === 0) {
    warnings.push({ code: 'EMPTY_DOCUMENT', message: 'Document has no lines' });
  }

  // ── 1. supply type ─────────────────────────────────────────────────────
  // NOTE ON THE UNRESOLVED CASE: `computeDocument` is a PURE function and stays
  // total — it always returns a computable document, because a live editor has
  // to render something while the user is still choosing a customer. When the
  // place of supply cannot be determined it charges as INTRA and emits a
  // `PLACE_OF_SUPPLY_UNRESOLVED` warning, and the WRITE path refuses to issue a
  // document carrying that warning.
  //
  // Defaulting to INTRA here is safe precisely because it is LOUD: the warning is
  // in the result, the service checks it, and the document cannot be issued
  // carrying it. The earlier alternative — falling back to the business state —
  // was quiet, and quiet is what made it wrong.
  const resolution = resolvePlaceOfSupply({
    businessStateCode: input.business.stateCode,
    customerStateCode: input.placeOfSupplyCode,
  });

  if (!resolution.resolved) {
    warnings.push({
      code: 'PLACE_OF_SUPPLY_UNRESOLVED',
      message: `Place of supply is required (${resolution.reason}). Select the supply state before issuing.`,
    });
  }

  const placeOfSupplyCode = resolution.resolved ? resolution.code : '';
  // The business state classifies the ALREADY-resolved code. It never chooses it.
  const supplyType: SupplyType = resolution.resolved ? resolution.supplyType : 'INTRA';

  // ── 2. provisional pass ────────────────────────────────────────────────
  const provisional = input.lines.map((l, i) => computeLine(l, i, supplyType, 0, []));

  // ── 3. document discount, pro-rata, EXACT ──────────────────────────────
  const netAfterLineDiscount = provisional.map((l) => l.grossPaise - l.discountPaise);
  const netTotal = netAfterLineDiscount.reduce((a, b) => a + b, 0);

  let invoiceDiscountPaise = ZERO;
  const invDisc = input.invoiceDiscount;
  if (invDisc && invDisc.type !== 'NONE' && netTotal > 0) {
    invoiceDiscountPaise =
      invDisc.type === 'PERCENT'
        ? percentOfMoney(asMoney(netTotal), invDisc.value)
        : clampNonNegative(asMoney(Math.trunc(invDisc.value)));
  }

  // Largest remainder: Σ allocated === invoiceDiscountPaise, always.
  const allocated = allocate(netAfterLineDiscount, invoiceDiscountPaise);

  // ── 4. final pass — tax on the post-allocation taxable value ───────────
  const lines = input.lines.map((l, i) => computeLine(l, i, supplyType, allocated[i] ?? 0, warnings));

  // ── 5. charges ─────────────────────────────────────────────────────────
  // PERCENT → milli-percent is ×1000 (1% = 1000 milli-%), NOT ×100.
  // Getting this wrong under-charges every percentage charge by 10×.
  const chargeAmount = (value: number, type: 'PERCENT' | 'AMOUNT'): Money =>
    type === 'PERCENT'
      ? percentOfMoney(asMoney(netTotal), value * 1_000)
      : clampNonNegative(asMoney(Math.trunc(value)));

  // A TAXABLE charge joins the taxable value, so it must also be TAXED.
  // Adding it to the taxable value without taxing it makes the invoice
  // internally inconsistent: tax would no longer be a function of the
  // printed taxable value, and a GST return would not reconcile.
  const highestLineRateBp = input.lines.reduce(
    (max, l) => (l.taxRateBp > max ? l.taxRateBp : max),
    0,
  );
  let chargeCgstPaise = ZERO;
  let chargeSgstPaise = ZERO;
  let chargeIgstPaise = ZERO;
  let chargeTaxRateBp = 0;

  const taxableChargesPaise = asMoney(
    input.charges
      .filter((c) => c.taxable)
      .reduce((s, c) => {
        const amount = chargeAmount(c.value, c.type);
        const rateBp = c.taxRateBp ?? highestLineRateBp;
        const t = splitGst(amount, rateBp, supplyType);
        chargeTaxRateBp = rateBp;
        chargeCgstPaise = asMoney(chargeCgstPaise + t.cgstPaise);
        chargeSgstPaise = asMoney(chargeSgstPaise + t.sgstPaise);
        chargeIgstPaise = asMoney(chargeIgstPaise + t.igstPaise);
        return s + amount;
      }, 0),
  );

  const nonTaxableChargesPaise = asMoney(
    input.charges.filter((c) => !c.taxable).reduce((s, c) => s + chargeAmount(c.value, c.type), 0),
  );

  // ── 6. aggregates — SUM OF ROUNDED line values ─────────────────────────
  const lineTaxablePaise = sumLines(lines, (l) => l.taxablePaise);
  const taxablePaise = asMoney(lineTaxablePaise + taxableChargesPaise);
  const cgstPaise = asMoney(sumLines(lines, (l) => l.cgstPaise) + chargeCgstPaise);
  const sgstPaise = asMoney(sumLines(lines, (l) => l.sgstPaise) + chargeSgstPaise);
  const igstPaise = asMoney(sumLines(lines, (l) => l.igstPaise) + chargeIgstPaise);
  const cessPaise = sumLines(lines, (l) => l.cessPaise);
  const taxTotalPaise = asMoney(cgstPaise + sgstPaise + igstPaise + cessPaise);
  const preRoundOffPaise = asMoney(taxablePaise + taxTotalPaise + nonTaxableChargesPaise);

  // ── 7. TCS (194Q) ──────────────────────────────────────────────────────
  const tcsPaise =
    input.tcs && taxablePaise >= input.tcs.thresholdPaise
      ? taxOnMoney(taxablePaise, input.tcs.rateBp)
      : ZERO;

  // ── 8. round-off ───────────────────────────────────────────────────────
  const roundOffPaise = input.roundOff
    ? computeRoundOff(preRoundOffPaise, input.roundOffMode)
    : ZERO;

  const grandTotalPaise = asMoney(preRoundOffPaise + tcsPaise + roundOffPaise);

  // ── 9. assemble ────────────────────────────────────────────────────────
  return Object.freeze({
    lines: Object.freeze(lines),
    grossSubtotalPaise: sumLines(lines, (l) => l.grossPaise),
    lineDiscountPaise: sumLines(lines, (l) => l.discountPaise),
    invoiceDiscountPaise,
    totalDiscountPaise: asMoney(sumLines(lines, (l) => l.discountPaise) + invoiceDiscountPaise),
    taxablePaise,
    taxableChargesPaise,
    nonTaxableChargesPaise,
    cgstPaise,
    sgstPaise,
    igstPaise,
    cessPaise,
    tcsPaise,
    taxTotalPaise,
    preRoundOffPaise,
    roundOffPaise,
    grandTotalPaise,
    totalQty: sumQuantity(lines.map((l) => l.quantity)),
    totalFreeQty: sumQuantity(lines.map((l) => l.freeQuantity)),
    taxSummary: Object.freeze(
      buildTaxSummary(
        lines,
        taxableChargesPaise > 0
          ? {
              rateBp: chargeTaxRateBp,
              taxablePaise: taxableChargesPaise,
              split: {
                cgstRateBp: 0,
                sgstRateBp: 0,
                igstRateBp: 0,
                cgstPaise: chargeCgstPaise,
                sgstPaise: chargeSgstPaise,
                igstPaise: chargeIgstPaise,
              } satisfies TaxSplit,
            }
          : undefined,
      ),
    ),
    amountInWords: numberToIndianWords(grandTotalPaise),
    supplyType,
    placeOfSupplyCode,
    calcVersion: CALC_VERSION,
    warnings: Object.freeze(warnings),
  });
}

/**
 * Round to a whole rupee (or ₹5) for the printed grand total.
 *
 * A compliance PRESENTATION device, not a calculation input: applied once at
 * document level, AFTER tax, and excluded from every tax bucket and every
 * ledger posting. That is why `taxTotalPaise` and `grandTotalPaise` are
 * separate fields on the result.
 */
export function computeRoundOff(
  preRoundOffPaise: Money,
  mode: 'NEAREST_RUPEE' | 'NEAREST_5' | 'NONE',
): Money {
  if (mode === 'NONE') return ZERO;
  const stepPaise = mode === 'NEAREST_5' ? 500 : 100;
  const rounded = roundPaise(preRoundOffPaise / stepPaise) * stepPaise;
  return asMoney(rounded - preRoundOffPaise);
}

